<template>
  <div>
    <template v-if="loading">{{ $t('voc.loading') }}</template>
    <template v-else>
      <div v-if="role !== 'employee'" style="margin-bottom: 54px">
        <div class="d-flex">
          <div class="adp-emp-card-for-manager">
            <div class="adp-emp-card-title">
              <span class="adp-h1">{{ employee.fullname }}</span>
              <button v-if="role !== 'admin'" class="btn adp-btn adp-btn__report"
                      @click="downloadEmployeeReport(employee.pernumber)">
                {{ $t('idp.download_report') }}
              </button>
            </div>
            <div class="adp-emp-info-for-manager">
              <div class="adp-emp-info-col">
                <span class="adp-p">{{ employee.pernumber }}</span>
                <span class="adp-p">{{ employee.position }}</span>
                <span class="adp-p">{{ employee.subdivision }}</span>
              </div>
              <div class="adp-emp-info-col" >
                <span class="adp-p adp-p__grey">{{ managers.length > 1 ? $t('voc.manager') : $t('voc.managers') }}:</span>
                <span class="adp-p" v-if="managers.length">{{ managers.join(', ') }}</span>
                <span v-else>{{ $t(`voc.managers_empty`) }}</span>
              </div>
              <div class="adp-emp-info-col" >
                <span class="adp-p adp-p__grey">
                  {{functionalManagers.length > 1 ? $t('voc.functional_manager') : $t('voc.functional_managers') }}:
                </span>
                <span class="adp-p" v-if="functionalManagers.length">{{ functionalManagers.join(', ') }}</span>
                <span v-else>{{ $t(`voc.functional_managers_empty`) }}</span>
              </div>
            </div>
          </div>
          <div>
            <div class="d-flex align-items-baseline adp-emp-card-for-manager--right-side">
              <span class="adp-h1" style="width: 60%">{{ $t('idp.individual_development_plan') }}&nbsp;</span>
              <span class="adp-h2">{{ $t('idp.for') }}&nbsp;</span>

              <my-year-filter :options="IDPYears" :init-selected="filterYears" :has-select-all="true"
                              v-on:my-change="changeYearListener">
              </my-year-filter>
            </div>
            <div class="d-flex" v-if="Object.keys(allIDPs).length === 1">
              <div class="adp-p adp-p__grey adp-status-tag mr-2" style="margin: 27px 0 0 45px"
                   :class="{'adp-status-tag__green': IDP.status === 'agreed_by_hr',
                 'adp-status-tag__red': IDP.status === 'on_approval' || IDP.status === 'not_formed'}">
                {{ $t(`idp.${IDP.status}`) }}
              </div>
              <button v-if="canSendToApproveIDP(role, IDP.status)" class="btn adp-btn mr-2"
                      style="margin: 27px 0 0 45px"
                      @click="sendToApproveIDP(defaultYear)">
                {{ $t('idp.send_to_approve_idp') }}
              </button>
              <button v-if="canApproveIDPAsManager(role, IDP.status)" class="btn adp-btn mr-2"
                      style="margin: 27px 0 0 45px"
                      @click="sendToAgreeIDP(defaultYear)">
                {{ $t('idp.assign_idp') }}
              </button>
              <button v-if="canApproveIDPAsHR(role, IDP.status)" class="btn adp-btn mr-2"
                      style="margin: 27px 0 0 45px"
                      @click="sendToAgreeIDP(defaultYear)">
                {{ $t('idp.assign_idp') }}
              </button>
            </div>
          </div>
        </div>

        <div class="tabs">
          <div
            v-for="(item, key) in tabs"
            :key="key"
            :class="{ 'tabs__item--active': item.selected }"
            @click="changeTab(item.type)"
            class="tabs__item">{{ item.name }}</div>
        </div>
      </div>

      <div v-if="role === 'employee'">

        <div class="d-flex align-items-baseline">
          <span class="adp-h1">{{ $t('idp.individual_development_plan') }}&nbsp;</span>
          <span class="adp-h2">{{ $t('idp.for') }}&nbsp;</span>

          <my-year-filter :options="IDPYears" :init-selected="filterYears" :has-select-all="true"
                          v-on:my-change="changeYearListener">
          </my-year-filter>
          <div class="adp-report" @click="downloadEmployeeReport(employee.pernumber)">
            <img src="/images/rectangle-yellow.svg" alt="">
            <img src="/images/download.svg" alt="" class="adp-report__download">
          </div>
        </div>
        <div class="d-flex">
          <div class="adp-p adp-p__grey adp-status-tag mr-2"
               :class="{'adp-status-tag__green': IDP.status === 'agreed_by_hr',
                 'adp-status-tag__red': IDP.status === 'on_approval' || IDP.status === 'not_formed'}">
            {{ $t(`idp.${IDP.status}`) }}
          </div>
          <button v-if="canSendToApproveIDP(role, IDP.status)" class="btn adp-btn ml-4"
                  @click="sendToApproveIDP()">
            {{ $t('idp.send_to_approve_idp') }}
          </button>
        </div>

        <div class="d-flex tabs">
          <div
              v-for="(item, key) in tabs"
              :key="key"
              :class="{ 'tabs__item--active': item.selected }"
              @click="changeTab(item.type)"
              class="tabs__item">{{ item.name }}
          </div>
        </div>


        <my-task-filter :options="IDPStatuses" :init-selected="filterStatuses" :has-select-all="true"
                        v-on:my-change="changeStatusListener"
                        style="margin-top: 32px; margin-bottom: 56px; cursor: pointer; width: 300px;">
        </my-task-filter>
      </div>

      <template v-for="year in reverseYears">
        <template v-if="Object.keys(allIDPs).length > 1">
          <div v-on:click="allIDPs[year].visible = !allIDPs[year].visible;" class="adp-h2 adp-collapse-control mb-4" >
            {{ $t('idp.learning_for') }} {{ year }} {{ $t('idp.y') }}

            <img :src="`/images/${allIDPs[year].visible ? 'minus' : 'plus'}.svg`" alt="">
            <div class="d-inline-flex">
              <div class="adp-p adp-p__grey adp-status-tag mr-2" style="margin: 0 24px 0 35px"
                   :class="{'adp-status-tag__green': getIdpStatus(year).status === 'agreed_by_hr',
                 'adp-status-tag__yellow': getIdpStatus(year).status === 'on_approval' || getIdpStatus(year).status === 'agreed_by_manager'}">
                {{ $t(`idp.${getIdpStatus(year).status}`) }}
              </div>

              <button v-if="canSendToApproveIDP(role, getIdpStatus(year).status)" class="btn adp-btn mr-2" @click="sendToApproveIDP(year)">
                {{ $t('idp.send_to_approve_idp') }}
              </button>
              <button v-if="canApproveIDPAsManager(role, getIdpStatus(year).status)" class="btn adp-btn mr-2"
                      @click="sendToAgreeIDP(year)">
                {{ $t('idp.assign_idp') }}
              </button>
              <button v-if="canApproveIDPAsHR(role, getIdpStatus(year).status)" class="btn adp-btn mr-2"
                      @click="sendToAgreeIDP(year)">
                {{ $t('idp.assign_idp') }}
              </button>
            </div>
          </div>
        </template>

        <div class="collapse" v-collapsible="allIDPs[year].visible">
          <div v-if="activeTab === 'learn'">
            <div class="adp-task-group">
              <span class="adp-h2">
                {{ $t('idp.mandatory') }}
                <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.mandatory_tooltip') }">
              </span>
              <div style="position: relative; display: inline">
                <button class="btn btn-primary adp-btn adp-btn__large" v-if="role !== 'employee' && role !== 'admin'"
                        @click="showActionsMenu('mandatoryAssignItems')">
                  {{ $t('voc.actions') }}
                </button>
                <div class="actions-button-error" v-if="showMandatoryAssignItemsError"
                     v-click-outside="() => {showMandatoryAssignItemsError = false}">
                  {{ $t('voc.warning_actions_activity') }}
                </div>
              </div>

              <template v-if="+year === defaultYear">
                <div class="btn adp-btn-outline float-right" v-if="role !== 'admin'" @click="addMandatoryCM = true">
                  + {{ $t('idp.add') }}
                </div>
                <div class="adp-task--crud-actions" style="top: 36px;" v-if="addMandatoryCM"
                     v-click-outside="() => {addMandatoryCM = false}" >
                  <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showIndividualCreateModal('mandatory')">
                    {{ $t('idp.create_individual') }}
                  </div>
                  <a class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
                     :href="`/catalog?group=mandatory&pernumber=${employee.pernumber}`">
                    {{ $t('idp.add_from_catalog') }}
                  </a>
                </div>
              </template>
            </div>
            <template v-if="allIDPs[year].learningActivities.filter(la => filterLa('mandatory', la.group, la.status)).length">
              <div v-if="role !== 'employee'">
                <employee-table-idp-learnings
                  :IDP="IDP" v-on:click-dots="openCM" v-on:show-files-modal="showFilesListModal"
                  v-on:show-comments-modal="showCommentsViewModal"
                  v-on:changeMandatoryAssignItems="changeMandatoryAssignItems"
                  v-on:learning-click="clickLearningItem"
                  :vals="allIDPs[year].learningActivities.filter(la => filterLa('mandatory', la.group, la.status))"
                />
              </div>
              <div id="mandatory-list" v-if="role === 'employee'">
                <div v-for="la in allIDPs[year].learningActivities.filter(la => filterLa('mandatory', la.group, la.status))">
                  <div id="drop-zone-mandatory-first"
                       v-if="allIDPs[year].learningActivities.filter(la => filterLa('mandatory', la.group, la.status)).indexOf(la) === 0"
                       style="display: none; width: 100%; height: 10px; border: 1px solid #E6C600; margin: 20px 0"
                       @dragover.prevent
                       @dragenter.prevent="dragLaEnter($event)"
                       @dragleave.prevent="dragLaLeave($event)"
                       @drop="dropLaToMandatory(la, allIDPs[year].learningActivities.filter(la => filterLa('mandatory', la.group, la.status)), true)"
                  >
                  </div>

                  <div class="adp-task"
                       id="draggable-item-mandatory"
                       :draggable="la.status === 'draft' && filterYears.length === 1"
                       @dragstart="dragLaStart($event, la)"
                       @dragend="dragLaEnd($event)"
                  >
                    <div class="adp-task--body">
                      <div class="adp-task--description">
                        <div class="adp-task--title">
                          <span class="adp-h3">{{ la.name }}</span>
                          <span class="adp-task-tag" v-tooltip.top-start="{ content: $t(`idp.${la.format_method}`) }">
                          {{ $t(`idp.${la.format_method}`) }}
                          </span>
                              </div>
                              <div>
                                <span class="adp-p">{{ $t('idp.competencies') }}:&nbsp;</span>
                                <span class="adp-p__grey">
                            {{ la.competencies.map(e => e.name).join(', ') }}
                          </span>
                        </div>
                      </div>
                      <div class="adp-task--files" @click="showFilesListModal(la.files, la.name, la.id)" v-if="la.files.length">
                        <img src="/images/paperclip.svg" v-if="isAllFilesViewed(la.files)" style="width: 24px; margin-right: 8px;" alt="">
                        <img src="/images/paperclip-yellow.svg" v-else style="width: 24px; margin-right: 8px;" alt="">
                        <span class="adp-p adp-p__green" :style="isAllFilesViewed(la.files) ? '' : 'color: #E6C600'">
                          {{ la.files.length }}
                        </span>
                      </div>
                      <div class="adp-task--comments" @click.stop="showCommentsViewModal(la.comments, la.name, la.id)" v-if="la.comments.length">
                        <img src="/images/comment.svg" v-if="isAllCommentsRead(la.comments)" style="margin-right: 8px;" alt="">
                        <img src="/images/comment-yellow.svg" v-else style="margin-right: 8px;" alt="">
                        <span class="adp-p adp-p__green" style="line-height: inherit"
                              :style="isAllCommentsRead(la.comments) ? '' : 'color: #E6C600'">
                          {{ la.comments.length }}
                        </span>
                      </div>
                      <div class="adp-task--kebab" @click="openCM($event, IDP.id, la)" >
                        <img src="/images/vertical_dots.svg" alt="">
                      </div>
                    </div>

                    <div class="adp-task--footer adp-task--footer__fe">
                      <div class="adp-task--dates">
                        <div class="adp-flex-column adp-task--date">
                          <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.when') }}</span>
                          <span class="adp-p">{{ formatDate(la.deadline) }}</span>
                        </div>
                        <div class="adp-flex-column adp-task--date">
                          <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.source') }}</span>
                          <span class="adp-p">{{ $t(`idp.${la.type}`) }}</span>
                        </div>
                      </div>
                      <span class="btn adp-assign-button adp-btn adp-btn__green mr-2" v-if="canCompleteActivity(la, IDP.status, role)"
                            @click="completeLearningActivity(la.id)">{{ $t('idp.complete') }}
                      </span>
                      <span class="adp-p adp-p__bold adp-flex-row adp-flex-row__end" :class="laStatusClass(la.status)"
                            style="width: 15%;  margin-left: auto; margin-right: 0">{{ $t(`idp.${la.status}`) }}
                      </span>

                    </div>
                  </div>

                  <div id="drop-zone-mandatory"
                       v-if="draggedActivity !== la"
                       style="display: none; width: 100%; height: 10px; border: 1px solid #E6C600; margin: 20px 0"
                       @dragover.prevent
                       @dragenter.prevent="dragLaEnter($event)"
                       @dragleave.prevent="dragLaLeave($event)"
                       @drop="dropLaToMandatory(la, allIDPs[year].learningActivities.filter(la => filterLa('mandatory', la.group, la.status)))"
                  >
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="adp-task">
                <div class="adp-task--body">
              <span class="adp-p adp-p__grey adp-p__bold">
                {{ $t('idp.not_found_mandatory') }}
              </span>
                </div>
              </div>
            </template>
            <div class="adp-task-group">
              <span class="adp-h2">
                {{ $t('idp.addition') }}
                <img src="/images/info.svg" style="margin: 0 31px" alt="info" v-tooltip.top-start="{ content: $t('idp.additional_tooltip') }">
              </span>
              <div style="position: relative; display: inline">
                <button class="btn btn-primary adp-btn adp-btn__large" v-if="role !== 'employee' && role !== 'admin'"
                        @click="showActionsMenu('additionalAssignItems')">
                  {{ $t('voc.actions') }}
                </button>
                <div class="actions-button-error" v-if="showAdditionalAssignItemsError"
                     v-click-outside="() => {showAdditionalAssignItemsError = false}">
                  {{ $t('voc.warning_actions_activity') }}
                </div>
              </div>
              <template v-if="+year === defaultYear">
                <div class="btn adp-btn-outline float-right" v-if="role !== 'admin'" @click="addAdditionalCM = true">
                  +{{ $t('idp.add') }}
                </div>
                <div class="adp-task--crud-actions" style="top: 36px;" v-if="addAdditionalCM"
                     v-click-outside="() => {addAdditionalCM = false}" >
                  <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showIndividualCreateModal('additional')">
                    {{ $t('idp.create_individual') }}
                  </div>
                  <a class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
                     :href="`/catalog?group=additional&pernumber=${employee.pernumber}`">
                    {{ $t('idp.add_from_catalog') }}
                  </a>
                </div>
              </template>
            </div>
            <template v-if="allIDPs[year].learningActivities.filter(la => filterLa('additional', la.group, la.status)).length">
              <div v-if="role !== 'employee'">
                <employee-table-idp-learnings
                  :IDP="IDP" v-on:click-dots="openCM" v-on:show-files-modal="showFilesListModal"
                  v-on:show-comments-modal="showCommentsViewModal"
                  v-on:changeAdditionalAssignItems="changeAdditionalAssignItems"
                  v-on:learning-click="clickLearningItem"
                  :vals="allIDPs[year].learningActivities.filter(la => filterLa('additional', la.group, la.status))"
                />
              </div>

              <div id="additional-list" v-if="role === 'employee'">
                <div v-for="la in allIDPs[year].learningActivities.filter(la => filterLa('additional', la.group, la.status))">
                  <div id="drop-zone-additional-first"
                       v-if="allIDPs[year].learningActivities.filter(la => filterLa('additional', la.group, la.status)).indexOf(la) === 0"
                       style="display: none; width: 100%; height: 10px; border: 1px solid #E6C600; margin: 20px 0"
                       @dragover.prevent
                       @dragenter.prevent="dragLaEnter($event)"
                       @dragleave.prevent="dragLaLeave($event)"
                       @drop="dropLaToAdditional(la, allIDPs[year].learningActivities.filter(la => filterLa('additional', la.group, la.status)), true)"
                  >
                  </div>

                  <div class="adp-task"
                       id="draggable-item-additional"
                       :draggable="la.status === 'draft' && filterYears.length === 1"
                       @dragstart="dragLaStart($event, la)"
                       @dragend="dragLaEnd($event)"
                  >
                    <div class="adp-task--body">
                      <div class="adp-task--description">
                        <div class="adp-task--title">
                          <span class="adp-h3">{{ la.name }}</span>
                          <span class="adp-task-tag" v-tooltip.top-start="{ content: $t(`idp.${la.format_method}`) }">
                          {{ $t(`idp.${la.format_method}`) }}
                          </span>
                              </div>
                              <div>
                                <span class="adp-p">{{ $t('idp.competencies') }}:&nbsp;</span>
                                <span class="adp-p__grey">
                            {{ la.competencies.map(e => e.name).join(', ') }}
                          </span>
                        </div>
                      </div>
                      <div class="adp-task--files" @click="showFilesListModal(la.files, la.name, la.id)" v-if="la.files.length">
                        <img src="/images/paperclip.svg" v-if="isAllFilesViewed(la.files)" style="width: 24px; margin-right: 8px;" alt="">
                        <img src="/images/paperclip-yellow.svg" v-else style="width: 24px; margin-right: 8px;" alt="">
                        <span class="adp-p adp-p__green" :style="isAllFilesViewed(la.files) ? '' : 'color: #E6C600'">
                          {{ la.files.length }}
                        </span>
                      </div>
                      <div class="adp-task--comments" @click.stop="showCommentsViewModal(la.comments, la.name, la.id)" v-if="la.comments.length">
                        <img src="/images/comment.svg" v-if="isAllCommentsRead(la.comments)" style="margin-right: 8px;" alt="">
                        <img src="/images/comment-yellow.svg" v-else style="margin-right: 8px;" alt="">
                        <span class="adp-p adp-p__green" style="line-height: inherit"
                              :style="isAllCommentsRead(la.comments) ? '' : 'color: #E6C600'">
                          {{ la.comments.length }}
                        </span>
                      </div>
                      <div class="adp-task--kebab" @click="openCM($event, IDP.id, la)" >
                        <img src="/images/vertical_dots.svg" alt="">
                      </div>
                    </div>

                    <div class="adp-task--footer adp-task--footer__fe">
                      <div class="adp-task--dates">
                        <div class="adp-flex-column adp-task--date">
                          <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.when') }}</span>
                          <span class="adp-p">{{ formatDate(la.deadline) }}</span>
                        </div>
                        <div class="adp-flex-column adp-task--date">
                          <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.source') }}</span>
                          <span class="adp-p">{{ $t(`idp.${la.type}`) }}</span>
                        </div>
                      </div>
                      <span class="btn adp-assign-button adp-btn__green adp-btn mr-2" v-if="canCompleteActivity(la, IDP.status, role)"
                            @click="completeLearningActivity(la.id)">{{ $t('idp.complete') }}
                      </span>
                      <span class="adp-p adp-p__bold adp-flex-row adp-flex-row__end" :class="laStatusClass(la.status)"
                            style="width: 15%;  margin-left: auto; margin-right: 0">{{ $t(`idp.${la.status}`) }}
                      </span>

                    </div>
                  </div>

                  <div id="drop-zone-additional"
                       v-if="draggedActivity !== la"
                       style="display: none; width: 100%; height: 10px; border: 1px solid #E6C600; margin: 20px 0"
                       @dragover.prevent
                       @dragenter.prevent="dragLaEnter($event)"
                       @dragleave.prevent="dragLaLeave($event)"
                       @drop="dropLaToAdditional(la, allIDPs[year].learningActivities.filter(la => filterLa('additional', la.group, la.status)))"
                  >
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="adp-task">
                <div class="adp-task--body">
              <span class="adp-p adp-p__grey adp-p__bold">
                {{ $t('idp.not_found_addition') }}
              </span>
                </div>
              </div>
            </template>
          </div>
          <div v-if="activeTab === 'medical'">
            <div class="adp-task-group">
              <span class="adp-h2">
                {{ $t('idp.mandatory_security') }}
                <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.medical_tooltip') }">
              </span>
              <div style="position: relative; display: inline">
                <button class="btn btn-primary adp-btn adp-btn__large" v-if="role !== 'employee'" @click="showActionsMenu('medicalAssignItems')">
                  {{ $t('voc.actions') }}
                </button>
                <div class="actions-button-error" v-if="showMedicalAssignItemsError"
                     v-click-outside="() => {showMedicalAssignItemsError = false}">
                  {{ $t('voc.warning_actions_activity') }}
                </div>
              </div>
            </div>

            <template v-if="allIDPs[year].learningActivities.filter(la => filterLa('mandatory_security', la.group, la.status)).length">
              <div v-if="role !== 'employee'">
                <employee-table-idp-medical-patents
                  :IDP="IDP" v-on:click-dots="openCM" v-on:show-files-modal="showFilesListModal"
                  v-on:show-comments-modal="showCommentsViewModal"
                  v-on:changeMedicalAssignItems="changeMedicalAssignItems"
                  v-on:learning-click="clickLearningItem"
                  :vals="allIDPs[year].learningActivities.filter(la => filterLa('mandatory_security', la.group, la.status))"/>
              </div>
              <div class="adp-task" v-if="role === 'employee'"
                   v-for="la in allIDPs[year].learningActivities.filter(la => filterLa('mandatory_security', la.group, la.status))">
                <div class="adp-task--body">
                  <div class="adp-task--description">
                    <div class="adp-task--title">
                      <span class="adp-h3">{{ la.name }}</span>
                      <span class="adp-task-tag" v-tooltip.top-start="{ content: $t(`idp.${la.format_method}`) }">
                      {{ $t(`idp.${la.format_method}`) }}
                    </span>
                    </div>
                    <div>
                      <span class="adp-p">{{ $t('idp.competencies') }}:&nbsp;</span>
                      <span class="adp-p__grey">
                      {{ la.competencies.map(e => e.name).join(', ') }}
                    </span>
                    </div>
                  </div>
                  <div class="adp-task--files" @click="showFilesListModal(la.files, la.name, la.id)" v-if="la.files.length">
                    <img src="/images/paperclip.svg" v-if="isAllFilesViewed(la.files)" style="width: 24px; margin-right: 8px;" alt="">
                    <img src="/images/paperclip-yellow.svg" v-else style="width: 24px; margin-right: 8px;" alt="">
                    <span class="adp-p adp-p__green" :style="isAllFilesViewed(la.files) ? '' : 'color: #E6C600'">
                          {{ la.files.length }}
                        </span>
                  </div>
                  <div class="adp-task--comments" @click.stop="showCommentsViewModal(la.comments, la.name, la.id)" v-if="la.comments.length">
                    <img src="/images/comment.svg" v-if="isAllCommentsRead(la.comments)" style="margin-right: 8px;" alt="">
                    <img src="/images/comment-yellow.svg" v-else style="margin-right: 8px;" alt="">
                    <span class="adp-p adp-p__green" style="line-height: inherit"
                          :style="isAllCommentsRead(la.comments) ? '' : 'color: #E6C600'">
                          {{ la.comments.length }}
                        </span>
                  </div>
                  <div class="adp-task--kebab" @click="openCM($event, IDP.id, la)" >
                    <img src="/images/vertical_dots.svg" alt="">
                  </div>
                </div>

                <div class="adp-task--footer adp-task--footer__fe">
                  <div class="adp-task--dates">
                    <div class="adp-flex-column adp-task--date">
                      <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.when') }}</span>
                      <span class="adp-p">{{ formatDate(la.deadline) }}</span>
                    </div>
                    <div class="adp-flex-column adp-task--date">
                      <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.source') }}</span>
                      <span class="adp-p">{{ $t(`idp.${la.type}`) }}</span>
                    </div>
                  </div>
                  <span class="btn adp-assign-button adp-btn adp-btn__green mr-2" v-if="canCompleteActivity(la, IDP.status, role)"
                        @click="completeLearningActivity(la.id)">{{ $t('idp.complete') }}</span>
                  <span class="adp-p adp-p__bold adp-flex-row adp-flex-row__end" :class="laStatusClass(la.status)"
                        style="width: 15%;  margin-left: auto; margin-right: 0">{{ $t(`idp.${la.status}`) }}
                  </span>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="adp-task">
                <div class="adp-task--body">
              <span class="adp-p adp-p__grey adp-p__bold">
                {{ $t('idp.not_found_mandatory_security') }}
              </span>
                </div>
              </div>
            </template>
          </div>
          <div v-if="activeTab === 'wish'">
            <div class="adp-task-group">
              <span class="adp-h2">{{ $t('idp.wishes_txt') }}
                <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.wishes_tooltip_employee') }"
                     v-if="role === 'employee'">
                <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.wishes_tooltip_hr') }"
                     v-if="role === 'hr'">
                <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.wishes_tooltip_manager') }"
                     v-if="role === 'manager'">
                <img src="/images/info.svg" style="margin: 0 31px" v-tooltip.top-start="{ content: $t('idp.wishes_tooltip_admin') }"
                     v-if="role === 'admin'">
              </span>
              <div style="position: relative; display: inline">
                <button class="btn btn-primary adp-btn adp-btn__large" v-if="role !== 'employee' && role !== 'admin'"
                        @click="showActionsMenu('wishAssignItems')">
                  {{ $t('voc.actions') }}
                </button>
                <div class="actions-button-error" v-if="showWishAssignItemsError"
                     v-click-outside="() => {showWishAssignItemsError = false}">
                  {{ $t('voc.warning_actions_activity') }}
                </div>
              </div>
              <template v-if="+year === defaultYear">
                <div class="btn adp-btn-outline float-right" v-if="role !== 'admin'" @click="$bvModal.show('desire-create-modal')">
                  + {{ $t('idp.add') }}
                </div>
              </template>
            </div>
            <template v-if="allIDPs[year].learningActivities.filter(la => filterLa('wishes', la.group, la.status)).length">
              <div v-if="role !== 'employee'">
                <employee-table-idp-wishes
                  :IDP="IDP" v-on:click-dots="openCM" v-on:show-files-modal="showFilesListModal"
                  v-on:show-comments-modal="showCommentsViewModal"
                  v-on:changeWishAssignItems="changeWishAssignItems"
                  v-on:learning-click="clickLearningItem"
                  :vals="allIDPs[year].learningActivities.filter(la => filterLa('wishes', la.group, la.status))"/>
              </div>
              <div class="adp-task" v-if="role === 'employee'"
                   v-for="la in allIDPs[year].learningActivities.filter(la => filterLa('wishes', la.group, la.status))">
                <div class="adp-task--body">
                  <div class="adp-task--description">
                    <div class="adp-task--title">
                      <span class="adp-h3">{{ la.name }}</span>
                    </div>
                    <div>
                      <span class="adp-p">{{ $t('catalog.description') }}:&nbsp;</span>
                      <span class="adp-p__grey">
                      {{ la.additional_info }}
                    </span>
                    </div>
                  </div>
                  <div class="adp-task--files" @click="showFilesListModal(la.files, la.name, la.id)" v-if="la.files.length">
                    <img src="/images/paperclip.svg" v-if="isAllFilesViewed(la.files)" style="width: 24px; margin-right: 8px;" alt="">
                    <img src="/images/paperclip-yellow.svg" v-else style="width: 24px; margin-right: 8px;" alt="">
                    <span class="adp-p adp-p__green" :style="isAllFilesViewed(la.files) ? '' : 'color: #E6C600'">
                          {{ la.files.length }}
                        </span>
                  </div>
                  <div class="adp-task--comments" @click.stop="showCommentsViewModal(la.comments, la.name, la.id)" v-if="la.comments.length">
                    <img src="/images/comment.svg" v-if="isAllCommentsRead(la.comments)" style="margin-right: 8px;" alt="">
                    <img src="/images/comment-yellow.svg" v-else style="margin-right: 8px;" alt="">
                    <span class="adp-p adp-p__green" style="line-height: inherit"
                          :style="isAllCommentsRead(la.comments) ? '' : 'color: #E6C600'">
                          {{ la.comments.length }}
                        </span>
                  </div>
                  <div class="adp-task--kebab" @click="openCM($event, IDP.id, la)" >
                    <img src="/images/vertical_dots.svg" alt="">
                  </div>
                </div>

                <div class="adp-task--footer adp-task--footer__fe">
                  <div class="adp-task--dates">
                    <div class="adp-flex-column adp-task--date">
                      <span class="adp-p adp-p__grey adp-task--meta-item-mw50">{{ $t('catalog.type') }}</span>
                      <span class="adp-p">{{ $t(`idp.${la.type}`) }}</span>
                    </div>
                  </div>
                  <span class="adp-p adp-p__bold adp-flex-row adp-flex-row__end" :class="laStatusClass(la.status)"
                        style="width: 15%;  margin-left: auto; margin-right: 0">{{ la.status === 'assigned' ? $t(`idp.assigned_for_wish`) : $t(`idp.${la.status}`) }}</span>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="adp-task">
                <div class="adp-task--body">
              <span class="adp-p adp-p__grey adp-p__bold">
                {{ $t('idp.not_found_wishes') }}
              </span>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>

      <!-- Context menu -->
      <div class="adp-task--crud-actions" :style="cmStyles" v-if="contextMenu.isVisible"
           v-click-outside="() => hideCM()" ref="ctxm">
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showViewModal()">
          {{ viewOrEditEvent(contextMenu.learningActivity) }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canAssignActivity(contextMenu.learningActivity, IDP.status, role)"
             @click="assignLearningActivity(contextMenu.learningActivityId)">
          {{ activeTab === 'wish' ? $t('idp.cm_assign') : $t('idp.cm_assign_hr') }}
        </div>
        <div v-if="canMarkAsDone(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="markAsDone(contextMenu.learningActivityId)"
        >
          {{ $t('idp.cm_mark_as_done') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="rejectLearningActivity(contextMenu.learningActivityId)"
             v-if="canRejectActivity(contextMenu.learningActivity, IDP.status, role)">
          {{ $t('idp.cm_reject') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="cancelLearningActivity(contextMenu.learningActivityId)"
             v-if="canCancelActivity(contextMenu.learningActivity, IDP.status, role)">
          {{ $t('idp.cm_cancel') }}
        </div>
        <div v-if="canChangeDeadline(contextMenu.learningActivity, IDP.status, role)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin" @click="showChangeDeadlineModal()">
          {{ $t('idp.cm_set_new_deadline') }}
        </div>
        <div v-if="canLeaveComment(role)" class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="showCommentsCreateModal()">
          {{ $t('idp.cm_comment') }}
        </div>
        <div v-if="canAttachFiles(role, contextMenu.learningActivityType)"
             class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="showAttachFilesModal()"
        >
          {{ $t('idp.cm_attach_files') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             @click="deleteLearningActivity(contextMenu.learningActivity)"
             v-if="canRemove(contextMenu.learningActivity, role)">
          {{ $t('idp.cm_delete') }}
        </div>
      </div>
      <!-- End of context menu -->

      <!-- Actions menu -->
      <div class="adp-task--crud-actions" v-if="canShowActionsMenu" :style="actionsMenuStyles"
           v-click-outside="() => hideActionsMenu()" ref="actions">
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassAssign(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('assigned')">
          {{ actionsMenu.currentType === 'wishAssignItems' ? $t('idp.cm_assign') : $t('idp.cm_assign_hr') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassReject(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('rejected')">
          {{ $t('idp.cm_reject') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassCancel(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('canceled')">
          {{ $t('idp.cm_cancel') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassDone(this[actionsMenu.currentType], IDP.status, role)"
             @click="massChangeStatusActivities('done')">
          {{ $t('idp.cm_mark_as_done') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassDeadlines(this[actionsMenu.currentType], IDP.status, role)"
             @click="setMassActivitiesDeadlines()">
          {{ $t('idp.cm_set_new_deadline') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassComments(role)"
             @click="addMassActivitiesComments()">
          {{ $t('idp.cm_comment') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassAttachFiles(this[actionsMenu.currentType], role)"
             @click="attachMassActivitiesFiles()">
          {{ $t('idp.cm_attach_files') }}
        </div>
        <div class="btn adp-btn adp-btn__t-left adp-btn__light adp-p__margin"
             v-if="canMassRemove(this[actionsMenu.currentType], role)"
             @click="massRemoveActivities()">
          {{ $t('idp.cm_delete') }}
        </div>
      </div>
      <!-- End of Actions menu -->
    </template>

    <desire-create-modal v-on:desire-created="reloadIDP()"></desire-create-modal>
    <desire-view-modal :learning-activity-id="Number(contextMenu.learningActivityId)"
                       :activity-id="Number(contextMenu.activityId)"
                       :activity-type="String(contextMenu.learningActivityType)"
                       :is-editable="true"
                       v-on:desire-edited="reloadIDP()">
    </desire-view-modal>

    <individual-create-modal :learning-activity-group="String(contextMenu.learningActivityGroup)"
                             :employee-type="employee ? String(employee.type) : ''"
                             v-on:individual-created="reloadIDP()">
    </individual-create-modal>
    <individual-view-modal :learning-activity-id="Number(contextMenu.learningActivityId)"
                           :activity-id="Number(contextMenu.activityId)"
                           :activity-type="String(contextMenu.learningActivityType)"
                           v-on:individual-updated="reloadIDP()" :is-editable="role !== 'admin'">
    </individual-view-modal>

    <common-event-view-modal :activity-id="Number(contextMenu.activityId)"
                             :activity-type="String(contextMenu.learningActivityType)"
                             :pernumber="employee ? Number(employee.pernumber) : 0"
                             :year="Number(contextMenu.year)"
                             :is-editable="role !== 'admin'">
    </common-event-view-modal>

    <comments-view-modal :comments="contextMenu.comments"
                         :name="contextMenu.activityName"
                         :viewer-pernumber="Number(this.pernumber)"
                         :learning-activity-id="Number(contextMenu.learningActivityId)">
    </comments-view-modal>
    <comments-create-modal :learning-activity-id="Number(contextMenu.learningActivityId)"
                           :name="contextMenu.activityName"
                           :viewer-pernumber="Number(this.pernumber)"
                           v-on:comment-created="reloadIDP()">
    </comments-create-modal >
    <files-list-modal :learning-activity-title="contextMenu.activityName"
                      :learning-activity-id="Number(contextMenu.learningActivityId)"
                      :viewer-pernumber="Number(this.pernumber)"
                      v-on:file-deleted="reloadIDP()"
                      :files="contextMenu.files">
    </files-list-modal>
    <attach-files-modal :learning-activity-title="contextMenu.activityName"
                        :learning-activity-id="Number(contextMenu.learningActivityId)"
                        :viewer-pernumber="Number(this.pernumber)"
                        v-on:files-uploaded="reloadIDP()">
    </attach-files-modal>
    <change-deadline-modal :activity="contextMenu.learningActivity ? contextMenu.learningActivity : {}"
                           v-on:deadline-updated="reloadIDP()">
    </change-deadline-modal>
    <div v-if="Object.values(actionsMenu.currentAssignedItems).length" v-for="(activity, i) in Object.values(actionsMenu.currentAssignedItems)">
      <change-deadline-modal :activity="activity" v-on:deadline-updated="reloadIDP()" :activity-index="i+1"/>
    </div>
    <mass-comments-create-modal :activities="[...Object.values(this.actionsMenu.currentAssignedItems)]"
                                :viewer-pernumber="Number(this.pernumber)"
                                v-on:comments-created="reloadIDP()"/>
    <mass-idp-files-attach-modal :activities="[...Object.values(this.actionsMenu.currentAssignedItems)]"
                                 :viewer-pernumber="Number(this.pernumber)"
                                 v-on:files-attached="reloadIDP()"/>
  </div>
</template>

<script>
import EmployeeSearch from "./EmployeeSearch";
import EmployeeSidebar from "./EmployeeSidebar";
import MyTaskFilter from "./MyTaskFilter";
import MyYearFilter from "./MyYearFilter";
import DesireCreateModal from "./modals/DesireCreateModal";
import DesireViewModal from "./modals/DesireViewModal";
import IndividualCreateModal from "./modals/IndividualCreateModal";
import IndividualViewModal from "./modals/IndividualViewModal";
import CommonEventViewModal from "./modals/CommonEventViewModal";
import CommentsViewModal from "./modals/CommentsViewModal";
import CommentsCreateModal from "./modals/CommentsCreateModal";
import FilesListModal from "./modals/FilesListModal";
import api from "../api";
import Vue from "vue";
import {each, find} from 'lodash';
import AttachFilesModal from "./modals/AttachFilesModal";
import ChangeDeadlineModal from "./modals/ChangeDeadlineModal";
import EmployeeTableIDPLearnings from "./EmployeeTableIDPLearnings";
import EmployeeTableIDPWishes from "./EmployeeTableIDPWishes";
import EmployeeTableIDPMedicalPatents from "./EmployeeTableIDPMedicalPatents";
import MassIdpFilesAttachModal from "./modals/MassIdpFilesAttachModal";
import MassCommentsCreateModal from "./modals/MassCommentsCreateModal";

export default {
  name: "IDP",
  props: {},
  data: function () {
    return {
      loading: true,
      dragged: false,
      showMandatoryAssignItemsError: false,
      showAdditionalAssignItemsError: false,
      showWishAssignItemsError: false,
      showMedicalAssignItemsError: false,

      employeePernumber: '',
      employee: null,
      managers: [],
      functionalManagers: [],
      IDP: null,
      learningActivities: [],
      allIDPs: {},

      defaultYear: 0,
      filterYears: [],
      IDPYears: [],

      displayDiv: true,

      filterStatuses: [],
      IDPStatuses: [],

      contextMenu: {
        isVisible: false,

        idpId: null,
        learningActivity: null,
        learningActivityId: null,
        activityId: null,
        learningActivityType: null,
        learningActivityStatus: null,

        learningActivityGroup: null,
        year: null,

        activityName: '',
        comments: [],
        files: [],

        top: 0,
        left: 0,
      },

      addMandatoryCM: false,
      addMandatorySecurityCM: false,
      addAdditionalCM: false,

      pernumber: 0,
      role: '',

			tabs: [
				{
					name: this.$t('idp.learn_tab'),
					selected: true,
					type: 'learn',
				},
				{
					name: this.$t('idp.wish_tab'),
					selected: false,
					type: 'wish',
				},
				{
					name: this.$t('idp.medical_tab'),
					selected: false,
					type: 'medical',
				}
			],

      draggedActivity: {},

      mandatoryAssignItems: {},
      additionalAssignItems: {},
      wishAssignItems: {},
      medicalAssignItems: {},

      actionsMenu: {
        isVisible: false,
        currentType: '',
        currentAssignedItems: {},

        top: 0,
        left: 0,
        right: 0,
      },

      reverseYears: [],
    };
  },
  components: {
    'employee-table-idp-medical-patents': EmployeeTableIDPMedicalPatents,
    'employee-table-idp-learnings': EmployeeTableIDPLearnings,
    'employee-table-idp-wishes': EmployeeTableIDPWishes,
    'change-deadline-modal': ChangeDeadlineModal,
    'attach-files-modal': AttachFilesModal,
    'employee-search': EmployeeSearch,
    'employee-sidebar': EmployeeSidebar,
    'my-task-filter': MyTaskFilter,
    'my-year-filter': MyYearFilter,
    'desire-create-modal': DesireCreateModal,
    'desire-view-modal': DesireViewModal,
    'individual-create-modal': IndividualCreateModal,
    'individual-view-modal': IndividualViewModal,
    'common-event-view-modal': CommonEventViewModal,
    'comments-view-modal': CommentsViewModal,
    'comments-create-modal': CommentsCreateModal,
    'files-list-modal': FilesListModal,
    'mass-comments-create-modal': MassCommentsCreateModal,
    'mass-idp-files-attach-modal': MassIdpFilesAttachModal,
  },
  computed: {
    cmStyles() {
      return {
        top: `${this.contextMenu.top}px`,
        left: `${this.contextMenu.left}px`,
        right: 'unset',
      };
    },
    actionsMenuStyles() {
      return {
        // top: '540px',
        top: `${this.actionsMenu.top}px`,
        left: `${this.actionsMenu.left}px`,
        right: 'unset',
      };
    },
		activeTab() {
			return find(this.tabs, tab => tab.selected).type;
		},
    canShowActionsMenu() {
      return this.actionsMenu.isVisible &&
        (Object.values(this.mandatoryAssignItems).length || Object.values(this.additionalAssignItems).length || Object.values(this.wishAssignItems).length)
    }
  },
  watch: {
    '$route.params.pernumber': function (pernumber) {
      this.getIdp(pernumber);
    }
  },
  methods: {
    openCM(event, idpId, learningActivity) {
      this.contextMenu.idpId = idpId;
      this.contextMenu.learningActivity = learningActivity;
      this.contextMenu.learningActivityId = learningActivity.id;
      this.contextMenu.activityId = learningActivity.activity_id;
      this.contextMenu.learningActivityStatus = learningActivity.status;
      this.contextMenu.learningActivityType = learningActivity.type;
      this.contextMenu.learningActivityGroup = learningActivity.group;
      this.contextMenu.year = learningActivity.year;
      this.contextMenu.activityName = learningActivity.name;
      this.contextMenu.files = learningActivity.files;

      this.contextMenu.isVisible = true;

      this.$nextTick(() => {
        this.contextMenu.top = event.pageY;
        this.contextMenu.left = event.pageX - this.$refs.ctxm.clientWidth;
      });
    },
    hideCM() {
      this.contextMenu.isVisible = false;
    },
    showViewModal() {
      if (this.contextMenu.learningActivityType === 'wishes') {
        this.$bvModal.show('desire-view-modal');
      } else if (this.contextMenu.learningActivityType === 'individual') {
        this.$bvModal.show('individual-view-modal');
      } else {
        this.$bvModal.show('common-event-view-modal');
      }
    },
    clickLearningItem(learningActivity) {
      this.contextMenu.learningActivityId = learningActivity.id;
      this.contextMenu.activityId = learningActivity.activity_id;
      this.contextMenu.learningActivityType = learningActivity.type;
      this.contextMenu.learningActivity = learningActivity;

      this.$nextTick(() => {
        return this.showViewModal();
      });
    },
    changeStatusListener(val) {
      this.filterStatuses = val;
    },
    changeYearListener(val) {
      if (val.length === 0) {
        val = [this.defaultYear];
      }
      this.filterYears = val;

      this.getIdp(this.employee.pernumber);
    },
    laStatusClass(status) {
      return {
        'adp-p__grey': status === 'draft',
        'adp-p__orange': status === 'assigned' || status === 'available',
        'adp-p__red': status === 'rejected',
        'adp-p__green': status === 'done',
      }
    },
    filterLa(targetGroup, group, status) {
      return targetGroup === group && (this.filterStatuses.includes(status) || this.filterStatuses.length === 0);
    },
    async getIdp(pernumber) {
      this.loading = true;
      try {
        let resp = await api.getIDP(pernumber, this.filterYears);
        let idp = resp.data;
        this.employee = idp.employee;
        this.managers = idp.managers;
        this.functional_managers = idp.functional_managers;
        this.IDP = {
          id: idp.activeIdp.id,
          status: idp.activeIdp.status,
        };

        this.IDPYears = idp.years.sort().map(year => {
          return {id: Number(year), code: String(year)}
        });
        let pair = {};
        // pair[this.defaultYear] = {visible: true, learningActivities: []};
        for (const [year, learningActivities] of Object.entries(idp.idps)) {
          pair[year] = {visible: true, learningActivities};
        }

        if(!pair[this.defaultYear]) {
          pair[this.defaultYear] = {visible: true, learningActivities: []};
        }

        this.reverseYears = Object.keys(pair).reverse();
        Vue.set(this, 'allIDPs', pair);
        this.idpStatuses = resp.data.statuses;
      } catch (e) {
        console.error(e);
      }
      this.loading = false;
    },
    formatDate(input) {
      if (!input) {
        return '';
      }

      let datePart = input.match(/\d+/g);
      const year = datePart[0];
      const month = datePart[1];
      const day = datePart[2];

      return day + '.' + month + '.' + year;
    },
    showIndividualCreateModal(group) {
      this.contextMenu.learningActivityGroup = group;
      this.$bvModal.show('individual-create-modal');
    },
    async showCommentsViewModal(comments, activityName, activityId) {
      this.contextMenu.comments = comments;
      this.contextMenu.activityName = activityName;
      this.contextMenu.learningActivityId = activityId;
      await this.$bvModal.show('comments-view-modal');
      // this.reloadIDP(); ?
    },
    showCommentsCreateModal() {
      this.$bvModal.show('comments-create-modal');
    },
    showChangeDeadlineModal() {
      this.$bvModal.show('change-deadline-modal');
    },
    reloadIDP() {
      this.$bvModal.hide('comments-create-modal');
      this.$bvModal.hide('comments-view-modal');
      this.$bvModal.hide('individual-create-modal');
      this.$bvModal.hide('individual-view-modal');
      this.$bvModal.hide('desire-create-modal');
      this.$bvModal.hide('desire-view-modal');
      this.$bvModal.hide('attach-files-modal');
      this.$bvModal.hide('mass-idp-files-attach-modal');
      this.getIdp(this.employee.pernumber);
    },
    moveToGroup(learningActivityId, newGroup, prePosition, postPosition) {
      (async () => {
        try {
          await api.moveToGroup(learningActivityId, newGroup, {prePosition, postPosition});
        } catch (e) {

        }
        this.reloadIDP();
      })();
    },
    assignLearningActivity(id) {
      (async () => {
        try {
          await api.assignLearningActivity(id);
        } catch (e) {

        }
        this.reloadIDP();
      })();
    },
    completeLearningActivity(id) {
      (async () => {
        try {
          await api.completeLearningActivity(id);
        } catch (e) {

        }
        this.reloadIDP();
      })();
    },
    deleteLearningActivity(wish) {
      (async () => {
        try {
          await api.deleteWish({id: wish.activity_id});
        } catch (e) {
          console.log(e);
        }
        this.reloadIDP();
        this.hideCM();
      })();
    },
    rejectLearningActivity(id) {
      (async () => {
        try {
          await api.rejectLearningActivity(id);
        } catch (e) {

        }
        this.reloadIDP();
        this.hideCM();
      })();
    },
    cancelLearningActivity(id) {
      (async () => {
        try {
          await api.cancelLearningActivity(id);
        } catch (e) {

        }
        this.reloadIDP();
        this.hideCM();
      })();
    },
    sendToApproveIDP() {
      (async () => {
        try {
          await api.sendToApprove();
        } catch (e) {

        }
        this.reloadIDP();
      })();
    },
    sendToAgreeIDP(year) {
      (async () => {
        try {
          await api.agreeIDP(this.role, this.employee.pernumber, year);
        } catch (e) {

        }
        this.reloadIDP();
      })();
    },
    async markAsDone(learningActivityId) {
        try {
          await api.completeLearningActivity(learningActivityId);
        } catch (e) {
          console.log(e);
        }

        this.reloadIDP();
    },
		changeTab(type) {
			each(this.tabs, tab => {
				tab.selected = false;
			});

			find(this.tabs, tab => tab.type === type).selected = true;

      // changing status filters for WISHES
      if (this.tabs[1].selected) {
        this.IDPStatuses = this.IDPStatuses.filter(status => status.id !== 'available' && status.id !== 'done');
        this.IDPStatuses.map(status => {
          if (status.id === 'assigned') status.wish = true;
        });
      } else {
        this.IDPStatuses = [];
        window.IDP.learningActivityStatuses.map(status => this.IDPStatuses.push({id: status, code: status}));
      }
		},
    async showFilesListModal(files, activityName, activityId) {
      this.contextMenu.files = files;
      this.contextMenu.activityName = activityName;
      this.contextMenu.learningActivityId = activityId;
      await this.$bvModal.show('files-list-modal');
      this.reloadIDP();
    },
    isAllFilesViewed(files) {
      const notAllFilesViewed = files.find(file => file.is_viewed === false)
      return this.role === 'employee' && !notAllFilesViewed;
    },
    isAllCommentsRead(comments) {
      const notAllCommentsRead = comments.find(comment => comment.is_read === false);
      return this.role === 'employee' && !notAllCommentsRead;
    },
    downloadEmployeeReport(pernumber) {
      api.export(pernumber).then(response => {
        const url = response.data.download_url;
        const link = document.createElement('a')
        link.href = url;
        const date = new Date().toLocaleDateString();
        link.setAttribute('download', `${date}-${pernumber}.xlsx`);
        link.click();
      });
    },
    getYear(activityId) {
      return find(this.allIDPs, year => find(year.learningActivities, activity => activity.id === activityId));
    },
    dragLaStart(event, activity) {
      this.dragged = true;
      this.draggedActivity = activity;
      const learningDiv = event.target;
      learningDiv.children[0].style.backgroundColor = '#F2ECC4';

      if (this.dragged) setTimeout(() => learningDiv.style.display = 'none', 0);

      document.querySelectorAll('#drop-zone-additional').forEach((item) => {
        item.style.display = 'block';
      });
      document.querySelectorAll('#drop-zone-additional-first').forEach((item) => {
        item.style.display = 'block';
      });

      document.querySelectorAll('#drop-zone-mandatory').forEach((item) => {
        item.style.display = 'block';
      });
      document.querySelectorAll('#drop-zone-mandatory-first').forEach((item) => {
        item.style.display = 'block';
      });
    },
    dragLaEnd(event) {
      const learningDiv = event.target;
      learningDiv.children[0].style.backgroundColor = '#fff';
      learningDiv.style.display = 'block';

      document.querySelectorAll('#draggable-item-mandatory').forEach((item) => {
        item.style.display = 'block';
      });
      document.querySelectorAll('#draggable-item-additional').forEach((item) => {
        item.style.display = 'block';
      });

      document.querySelectorAll('#drop-zone-additional').forEach((item) => {
        item.style.display = 'none';
      });
      document.querySelectorAll('#drop-zone-additional-first').forEach((item) => {
        item.style.display = 'none';
      });
      document.querySelectorAll('#drop-zone-mandatory').forEach((item) => {
        item.style.display = 'none';
      });
      document.querySelectorAll('#drop-zone-mandatory-first').forEach((item) => {
        item.style.display = 'none';
      });

      this.dragged = false;
    },
    dragLaEnter(event) {
      if (event.target.closest('#drop-zone-additional'))
        event.target.closest('#drop-zone-additional').style.backgroundColor = '#F2ECC4';
      if (event.target.closest('#drop-zone-additional-first'))
        event.target.closest('#drop-zone-additional-first').style.backgroundColor = '#F2ECC4';

      if (event.target.closest('#drop-zone-mandatory'))
        event.target.closest('#drop-zone-mandatory').style.backgroundColor = '#F2ECC4';
      if (event.target.closest('#drop-zone-mandatory-first'))
        event.target.closest('#drop-zone-mandatory-first').style.backgroundColor = '#F2ECC4';
    },
    dragLaLeave(event) {
      if (event.target.closest('#drop-zone-additional'))
        event.target.closest('#drop-zone-additional').style.backgroundColor = '#fff';
      if (event.target.closest('#drop-zone-additional-first'))
        event.target.closest('#drop-zone-additional-first').style.backgroundColor = '#fff';

      if (event.target.closest('#drop-zone-mandatory'))
        event.target.closest('#drop-zone-mandatory').style.backgroundColor = '#fff';
      if (event.target.closest('#drop-zone-mandatory-first'))
        event.target.closest('#drop-zone-mandatory-first').style.backgroundColor = '#fff';
    },
    dropLaToMandatory(la, vals, firstItem = false) {
      if (this.canMoveToMandatory(this.draggedActivity.group, this.draggedActivity.status)) {
        let preItemPosition = vals[vals.indexOf(la)].position;
        let postItemPosition = null;
        if (vals.indexOf(la) !== vals.length-1) {
          postItemPosition = vals[vals.indexOf(la) + 1].position;
        }
        if (firstItem) {
          preItemPosition = null;
          postItemPosition = vals[vals.indexOf(la)].position;
        }

        this.moveToGroup(this.draggedActivity.id, 'mandatory', preItemPosition, postItemPosition);
      }
      this.draggedActivity = {};
    },
    dropLaToAdditional(la, vals, firstItem = false) {
      if (this.canMoveToAdditional(this.draggedActivity.group, this.draggedActivity.status)) {
        let preItemPosition = vals[vals.indexOf(la)].position;
        let postItemPosition = null;
        if (vals.indexOf(la) !== vals.length-1) {
          postItemPosition = vals[vals.indexOf(la) + 1].position;
        }
        if (firstItem) {
          preItemPosition = null;
          postItemPosition = vals[vals.indexOf(la)].position;
        }

        this.moveToGroup(this.draggedActivity.id, 'additional', preItemPosition, postItemPosition);
      }
      this.draggedActivity = {};
    },
    showAttachFilesModal() {
      this.$bvModal.show('attach-files-modal');
    },
    showActionsMenu(type) {
      if (type && !Object.values(this[type]).length) {
        if (type === 'mandatoryAssignItems') this.showMandatoryAssignItemsError = true;
        if (type === 'additionalAssignItems') this.showAdditionalAssignItemsError = true;
        if (type === 'wishAssignItems') this.showWishAssignItemsError = true;
        if (type === 'medicalAssignItems') this.showMedicalAssignItemsError = true;

        setTimeout(() => {
          this.showMandatoryAssignItemsError = false;
          this.showAdditionalAssignItemsError = false;
          this.showWishAssignItemsError = false;
          this.showMedicalAssignItemsError = false;
        }, 2000)
        return;
      }
      this.actionsMenu.isVisible = true;
      this.actionsMenu.currentType = type;
      this.actionsMenu.currentAssignedItems = this[type];

      this.$nextTick(() => {
        this.actionsMenu.left = Number(event.pageX) - 50;
        this.actionsMenu.top = Number(event.pageY) + 30;
      });
    },
    hideActionsMenu() {
      this.actionsMenu.isVisible = false;
    },
    changeMandatoryAssignItems(items) {
      this.mandatoryAssignItems = items;
    },
    changeAdditionalAssignItems(items) {
      this.additionalAssignItems = items;
    },
    changeWishAssignItems(items) {
      this.wishAssignItems = items;
    },
    changeMedicalAssignItems(items) {
      this.medicalAssignItems = items;
    },
    async massChangeStatusActivities(status) {
      let data;
      if (this.actionsMenu.currentType === 'mandatoryAssignItems') data = { ids: Object.keys(this.mandatoryAssignItems), status: status };
      if (this.actionsMenu.currentType === 'additionalAssignItems') data = { ids: Object.keys(this.additionalAssignItems), status: status  };
      if (this.actionsMenu.currentType === 'wishAssignItems') data = { ids: Object.keys(this.wishAssignItems), status: status  };
      if (this.actionsMenu.currentType === 'medicalAssignItems') data = { ids: Object.keys(this.medicalAssignItems), status: status  };
      await api.massActivityChangeStatus(data);
      await this.reloadIDP();
    },
    async setMassActivitiesDeadlines() {
      this.$bvModal.show('change-deadline-modal');
    },
    async addMassActivitiesComments() {
      this.$bvModal.show('mass-comments-create-modal');
    },
    async attachMassActivitiesFiles() {
      this.$bvModal.show('mass-idp-files-attach-modal');
    },
    async massRemoveActivities() {
      const ids = Object.values(this.wishAssignItems).map(wish => wish.activity_id);
      await api.massDeleteWishes({ids});
      this.reloadIDP();
      this.hideCM();
    },
    getIdpStatus(year) {
      return this.idpStatuses.find(x => x.year === Number(year));
    },
    viewOrEditEvent(event) {
      if (event.author_role === this.role) return this.$t('idp.cm_view_edit');
      if (this.role === 'hr') {
        if (event.type === 'individual' && event.author_role !== this.role) return this.$t('idp.cm_view');
        else return this.$t('idp.cm_view_edit');
      }
      return this.$t('idp.cm_view');
    }
  },
  mounted() {
    this.defaultYear = window.IDP.currentYear;
    this.role = window.IDP.role ? String(window.IDP.role) : 'employee';


    let laStatusArr = window.IDP.learningActivityStatuses;
    for (let status of laStatusArr) {
      this.IDPStatuses.push({id: status, code: status,},)
    }

    let currState = Object.fromEntries(new URLSearchParams(window.location.search));

    let filterYears = [];
    if (currState.la_y) {
      filterYears = currState.la_y.split('_').map(year => Number(year))
    }
    filterYears.push(this.defaultYear);
    let uniqFilterYears = Array.from(new Set(filterYears)).sort();
    this.filterYears.splice(0, this.filterYears.length, ...uniqFilterYears);

    if (currState.la_s) {
      let filterStatuses = currState.la_s.split('0');
      filterStatuses = filterStatuses.filter(status => laStatusArr.includes(status));
      this.filterStatuses.splice(0, this.filterStatuses.length, ...filterStatuses);
    }

    this.pernumber = window.IDP.pernumber ? Number(window.IDP.pernumber) : 0;

    let employeePernumber = '0';
    if (this.$route && this.$route.params.pernumber) {
      employeePernumber = this.$route.params.pernumber;
    } else if (window.IDP.pernumber && window.IDP.role === 'admin') {
      employeePernumber = window.IDP.pernumber;
    } else if (window.IDP.pernumber && (window.IDP.role === '' || window.IDP.role === 'employee')) {
      employeePernumber = window.IDP.pernumber;
    }

    this.getIdp(employeePernumber);

    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'files-list-modal') {
        api.getActivityFiles(this.contextMenu.learningActivityId)
        .then(response => this.contextMenu.files = response.data)

        this.contextMenu.files.forEach(async (file) => {
          await api.setFileViewed(file.id);
        })
      }
      if (modalId === 'comments-view-modal') {
        api.getActivityComments(this.contextMenu.learningActivityId)
          .then(response => this.contextMenu.comments = response.data)

        this.contextMenu.comments.forEach(async (comment) => {
          await api.readComment(comment.id);
        })
      }
    })
  }
}
</script>

<style scoped>

</style>
